import React from "react";
import { Link } from "react-router-dom";
import { LegalShell } from "./Terms";

const SECTIONS = [
  {
    h: "1. Summary",
    p: `Fount Stories offers monthly and annual subscription plans (Solo, Pro, Studio). You can cancel any time from your Billing page — you keep full access until the end of your paid period, then automatically move to the Free plan. New subscribers are covered by our 7-day satisfaction guarantee. This page explains exactly how cancellations, refunds, chargebacks, and disputes are handled.`,
  },
  {
    h: "2. Cancelling your subscription",
    p: `Sign in to Fount, open Account → Billing, and click "Cancel". Cancellation is scheduled at the end of your current billing period — you retain full paid access (Daily Brief, higher generation limits, saved packets) until that date and then automatically move to Free. Your saved packets, calendars, profile, and payment history remain intact after the downgrade. You can resume the subscription with one click any time before the period ends.`,
  },
  {
    h: "3. 7-day satisfaction guarantee (new subscribers)",
    p: `If you subscribe to a paid plan for the first time and it isn't the right fit, email support@fountstories.com within 7 days of your first payment with the subject "Refund" and we will fully refund that first payment — no questions asked. This guarantee is limited to your first paid subscription per account and does not apply to repeat cancellations, upgrades, or renewals.`,
  },
  {
    h: "4. Annual-plan prorated refunds",
    p: `Annual subscribers who cancel more than 7 days after purchase are eligible for a prorated refund of unused months, provided the cancellation is requested within 30 days of the annual charge. Prorated refunds are calculated as the annual price divided by 12 and multiplied by the number of full unused months. After 30 days no refund is available on the annual charge, but you can still cancel and retain access through the end of the paid year.`,
  },
  {
    h: "5. Monthly-plan refunds",
    p: `Monthly subscriptions are not refunded except (a) within the 7-day satisfaction guarantee window described above, (b) where required by consumer law in your jurisdiction, or (c) in cases of duplicate charges or verified billing errors. Because you retain access until the end of your paid month after cancelling, most cancellation requests don't require a refund at all.`,
  },
  {
    h: "6. Failed renewal payments",
    p: `If a renewal payment fails, Stripe automatically retries the charge up to three times over seven days. During this dunning period we email you asking to update your payment method. If all retries fail, your subscription is cancelled and your account moves to the Free plan. Access already used will not be billed retroactively.`,
  },
  {
    h: "7. Upgrades and downgrades",
    p: `Upgrading between paid plans (e.g. Solo → Pro) applies immediately and is billed on a prorated basis by Stripe — you pay only the difference for the remainder of your current period. Downgrading between paid plans takes effect at the end of your current billing period; no refund is issued for the current period.`,
  },
  {
    h: "8. Duplicate charges and billing errors",
    p: `If you see a duplicate charge or believe you were incorrectly billed, please email support@fountstories.com within 60 days of the charge with your receipt or Stripe payment reference. Verified errors are refunded to the original payment method within 5–10 business days.`,
  },
  {
    h: "9. Chargebacks and disputes",
    p: `Please contact us before initiating a chargeback — we resolve nearly all issues within one business day at support@fountstories.com. Chargebacks filed without contacting us may result in immediate account suspension while the dispute is investigated. If Stripe rules the chargeback invalid, the associated fees are the account holder's responsibility and the account may be permanently disabled to protect the platform from repeat abuse.`,
  },
  {
    h: "10. How to request a refund",
    p: `Email support@fountstories.com with the subject line "Refund", the email address on your Fount account, and the reason for the request. Refund decisions are made within 3 business days. Approved refunds are issued to the original payment method via Stripe and typically appear on your statement within 5–10 business days.`,
  },
  {
    h: "11. Free plan and preview periods",
    p: `The Free plan is offered at no cost and has no refund implications. During any preview or beta period during which paid features are provided without charge, no refund obligation applies — access can be revoked at any time.`,
  },
  {
    h: "12. Changes to this policy",
    p: `We may update this policy from time to time. Material changes are announced by email and take effect no sooner than 7 days after notice. Refund requests are evaluated under the policy in effect at the time your subscription was purchased.`,
  },
  {
    h: "13. Contact",
    p: `All refund and cancellation requests: support@fountstories.com. We respond within one business day.`,
  },
];

export default function RefundPolicy() {
  return (
    <LegalShell title="Refund & Cancellation Policy" lastUpdated="July 1, 2026">
      <p className="text-sm text-gray-600 leading-relaxed" data-testid="refund-policy-intro">
        This policy applies to all paid Fount Stories subscriptions. It supplements our{" "}
        <Link to="/terms" className="underline hover:text-black">Terms of Service</Link> and{" "}
        <Link to="/privacy" className="underline hover:text-black">Privacy Policy</Link>. Please read it carefully before subscribing.
      </p>

      {/* Quick-reference card */}
      <div className="mt-8 rounded-2xl border border-[var(--border)] bg-white p-6" data-testid="refund-quick-reference">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3">At a glance</div>
        <ul className="space-y-2 text-[14px] text-gray-700 leading-relaxed">
          <li>· <strong>Cancel anytime</strong> — full access until the end of your paid period, then automatic move to Free.</li>
          <li>· <strong>7-day satisfaction guarantee</strong> on your first paid subscription. Full refund, no questions asked.</li>
          <li>· <strong>Annual plans</strong> — prorated refund of unused months if requested within 30 days.</li>
          <li>· <strong>Contact support first</strong> before filing a chargeback: <a href="mailto:support@fountstories.com" className="underline hover:text-black">support@fountstories.com</a></li>
        </ul>
      </div>

      <div className="mt-10 space-y-9">
        {SECTIONS.map((s) => (
          <section key={s.h} data-testid={`refund-section-${s.h.split(".")[0]}`}>
            <h2 className="font-display text-2xl tracking-[-0.02em] font-medium leading-tight">{s.h}</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed mt-3">{s.p}</p>
          </section>
        ))}
      </div>
    </LegalShell>
  );
}
